"use client";

import Link from "@/components/ui/CcLink";
import { CalendarDays } from "lucide-react";

import type { BookCallPopupVariant } from "@/lib/book-call-popup-copy";
import { shouldOpenBookCallPopup } from "@/lib/book-call-cta-label";
import { useBookCallTriggerProps } from "@/lib/use-book-call-trigger-props";

type CcButtonProps = {
  href?: string;
  onClick?: () => void;
  /** Opens the native booking dialog instead of navigating. */
  bookCallPopup?: boolean;
  /** Popup sidebar / booking form copy variant when `bookCallPopup` is true. */
  bookCallVariant?: BookCallPopupVariant;
  variant?: "primary" | "dark" | "outline" | "ghost";
  label: string;
  hoverLabel?: string;
  expand?: boolean;
  expandCompact?: boolean;
  leadingIcon?: "arrow" | "calendar";
  type?: "button" | "submit";
  form?: string;
  className?: string;
  loading?: boolean;
  disabled?: boolean;
};

function CcButtonArrowIcon() {
  return (
    <svg
      className="cc-btn__icon"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 256 256"
      aria-hidden="true"
    >
      <path
        d="M220.24,132.24l-72,72a6,6,0,0,1-8.48-8.48L201.51,134H40a6,6,0,0,1,0-12H201.51L139.76,60.24a6,6,0,0,1,8.48-8.48l72,72A6,6,0,0,1,220.24,132.24Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function CcButton({
  href,
  onClick,
  bookCallPopup = false,
  bookCallVariant = "default",
  variant = "primary",
  label,
  hoverLabel,
  expand = false,
  expandCompact = false,
  leadingIcon = "arrow",
  type = "button",
  form,
  className = "",
  loading = false,
  disabled = false,
}: CcButtonProps) {
  const opensBookCall = shouldOpenBookCallPopup(bookCallPopup, label);
  const bookCallTriggerProps = useBookCallTriggerProps(
    opensBookCall
      ? {
          bookCallIcon: expand ? leadingIcon : undefined,
          bookCallVariant,
        }
      : undefined,
  );
  const isDisabled = disabled || loading;
  const classes = `cc-btn cc-btn--${variant} ${expand ? "cc-btn--expand" : ""} ${expand && expandCompact ? "cc-btn--expand-compact" : ""} ${hoverLabel ? "cc-btn__swap" : ""} ${loading ? "cc-btn--loading" : ""} ${className}`.trim();

  const content = expand ? (
    <>
      <span className="cc-btn__icon-wrap" aria-hidden="true">
        <span className="cc-btn__icon-stack">
          <span className="cc-btn__icon-slot cc-btn__icon-slot--arrow">
            {leadingIcon === "calendar" ? (
              <CalendarDays className="cc-btn__icon" strokeWidth={1.8} />
            ) : (
              <CcButtonArrowIcon />
            )}
          </span>
          <span className="cc-btn__icon-slot cc-btn__icon-slot--spinner">
            <span className="cc-btn__spinner" />
          </span>
        </span>
      </span>
      <span className="cc-btn__label-wrap">
        <span className="cc-btn__label">{label}</span>
      </span>
    </>
  ) : hoverLabel ? (
    <>
      <span className="cc-btn__label">{label}</span>
      <span className="cc-btn__label-hover" aria-hidden="true">
        {hoverLabel}
      </span>
    </>
  ) : (
    label
  );

  if (opensBookCall) {
    return (
      <button
        type="button"
        className={classes}
        {...bookCallTriggerProps}
        onClick={onClick}
        disabled={isDisabled}
        aria-busy={loading || undefined}
      >
        {content}
      </button>
    );
  }

  if (href) {
    return (
      <Link href={href} className={classes} onClick={onClick}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      form={form}
      onClick={onClick}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading || undefined}
    >
      {content}
    </button>
  );
}
